import { Component, ElementRef, Inject, OnDestroy, OnInit } from '@angular/core';
import {
    disconnectExpandedDetails,
    observeExpandedDetail,
} from 'src/app/shared/expanded-detail-resizer';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { take } from 'rxjs/operators';

import { DOCUMENT, DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { Role } from 'src/app/enums/role';
import { TransactionStatus } from 'src/app/enums/transaction.status';
import { Employee } from 'src/app/model/employee';
import { Pagination } from 'src/app/model/Pagination';
import {
    BalanceSnapshot,
    balanceAmount,
    isBalanceAvailable,
    UNAVAILABLE_DISPLAY,
} from 'src/app/model/cashback-allocation-balance';
import { Transaction } from 'src/app/model/transaction';
import { CommonUtil } from 'src/app/services/common.util';
import { CompanyService } from 'src/app/services/company.service';
import { EnterpriseService } from 'src/app/services/enterprise.service';
import { LoginService } from 'src/app/services/login.service';
import { StorageConstant } from 'src/app/settings/storage-constants';

@Component({
    selector: 'app-enterprise-transactions',
    templateUrl: './enterprise-transactions.component.html',
    styleUrls: [
        './enterprise-transactions.component.css',
        // LP-5360: shared with the admin transaction detail so the same ledger renders identically
        // in both views rather than through two copies that can drift.
        '../../shared/styles/cashback-allocation-table.css',
        '../../shared/styles/transaction-detail-layout.css',
    ],
})
export class EnterpriseTransactionsComponent implements OnInit, OnDestroy {
    /**
     * LP-5360: the marker for a value the backend does not have. Never `0.00`.
     *
     * <p>Shared with the admin transaction detail (LP-5358) rather than redefined, so the two views
     * cannot drift into showing absence differently.
     */
    unavailableDisplay = UNAVAILABLE_DISPLAY;

    id: string;
    display = false;
    displaySuccess = false;
    displayRefund = 'none';
    eMessage = '';
    sMessage = '';
    staffId: string;
    pin: string;
    transaction: Transaction = new Transaction();
    accountTypeDropdown: string[] = [];
    transactionStatusDropdown: string[] = [];
    transactionTypeDropdown: string[] = [];
    form: FormGroup;
    invalidForm = false;
    transactionList: Array<Transaction>;
    excelList = new Array<any>();
    pagination = new Pagination();
    showOptions = false;
    search = false;
    searchItems: string[] = [];
    status = TransactionStatus;
    employee: Employee;
    userRole = Role.enterprise_contact.toString();
    role = Role;
    sessionUser$ = this.loginService.sessionUser.asObservable();

    constructor(
        private router: Router,
        private elementRef: ElementRef<HTMLElement>,
        private formBuilder: UntypedFormBuilder,
        public commonUtil: CommonUtil,
        public loginService: LoginService,
        @Inject(DOCUMENT) private document: Document,
        private enterpriseService: EnterpriseService,
        private datePipe: DatePipe,
        public companyService: CompanyService,
    ) {
        this.form = this.formBuilder.group({
            enterpriseId: [''],
            enterpriseStaffId: [''],
            text: [''],
            start: [''],
            length: [''],
        });
    }

    ngOnInit() {
        this.sessionUser$
            .pipe(take(1)) // take only once to avoid memory leaks or duplicate requests
            .subscribe((user) => {
                if (user?.enterprise) {
                    this.id = user.enterprise.enterpriseId;
                    this.userRole = user.role;
                    this.getData();
                }
            });

        this.employee = JSON.parse(localStorage.getItem(StorageConstant.EMPLOYEE_NUMBER));
        localStorage.removeItem(StorageConstant.EMPLOYEE_NUMBER);
    }

    getData() {
        // LP-5360: `search = false` destroys the results section immediately, so the rows being
        // observed are detached right here -- before any response arrives. Cleaning up in the
        // success callback missed the error path entirely and could fire after ngOnDestroy.
        // Codex PR #114, round 3.
        disconnectExpandedDetails(this.elementRef.nativeElement);

        this.search = false;
        this.form.get('enterpriseId').setValue(this.id);
        if (this.employee) {
            this.form.get('enterpriseStaffId').setValue(this.employee.enterpriseStaffId);
        }
        this.form.get('start').setValue(this.pagination.start);
        this.form.get('length').setValue(this.pagination.length);
        this.enterpriseService.getEmployeeTransactionHistory(this.form).subscribe(
            (res) => {
                setTimeout(() => {
                    // ...and again here, not instead of the call in getData(). Two getData()
                    // calls can overlap: the second one's pre-request cleanup may run BEFORE this
                    // callback from the first renders its rows. If the user expands one of those
                    // rows, replacing the list below would detach it with nothing having released
                    // its observer. Cleaning up at both moments -- when the rows are hidden and
                    // when they are replaced -- closes that window without having to sequence or
                    // cancel in-flight requests. Codex PR #114, round 4.
                    disconnectExpandedDetails(this.elementRef.nativeElement);

                    this.transactionList =
                        res && res.data
                            ? res.data.map((t) => new Transaction().deserialize(t))
                            : [];

                    this.excelList = new Array<any>();

                    if (this.transactionList) {
                        for (const tran of this.transactionList) {
                            this.excelList.push(this.getExcel(tran));
                        }
                    }
                    this.pagination.total = !res.total ? 0 : res.total;
                    this.search = true;
                }, 350);
            },
            (error) => {
                this.search = true;
            },
        );
    }

    public getExcel(tran: Transaction) {
        const excel = {
            'From User': tran.fromUserName,
            'To User': tran.toUserName,
            'Transaction Code': tran.transactionCode,
            Type: tran.type,
            Amount: tran.amount,
            Fee: tran.fees,
            Cashback: tran.cashback,
            'Cashback Fee': tran.cashbackFees,
            Status: tran.status,
            Date: this.datePipe.transform(tran.date, 'yyyy-MM-dd'),
            Description: tran.description,
        };

        return excel;
    }

    searchUsers(text: string) {
        this.pagination.text = text;
        this.getData();
    }

    clearText() {
        this.form.get('text').setValue('');
    }

    receiveFilter(data: { name: string; value: number }) {}

    changeLength(len: number) {
        this.pagination.length = len;
        this.pagination.start = 0;
        this.getData();
    }

    changePage(page: number) {
        this.pagination.changePage(page);
        this.getData();
    }

    closeModel(event: any) {
        this.display = false;
        this.displaySuccess = false;
    }

    get controls(): any {
        return this.form.controls;
    }

    submit() {
        this.getData();
    }

    /** LP-5360: see transaction-body -- observers must not outlive the rows. Codex PR #114. */
    ngOnDestroy() {
        disconnectExpandedDetails(this.elementRef.nativeElement);
    }

    showDetails(val: string, logo: string) {
        let element = this.document.getElementById(val);
        if (element.style.maxHeight) {
            element.style.maxHeight = null;
            element.style.border = 'none';
        } else {
            element.style.maxHeight = element.scrollHeight + 'px';
            element.style.border = '6px';
            // LP-5360: see transaction-body -- the responsive detail grows as it narrows, so an
            // open row must re-measure rather than keep its original height. Codex PR #114, P2.
            observeExpandedDetail(element);
        }

        element = this.document.getElementById(logo);
        let src = element.getAttribute('src');
        src =
            src === 'assets/images/default-transaction-detail.png'
                ? 'assets/images/hover-transaction-detail.png'
                : 'assets/images/default-transaction-detail.png';

        element.setAttribute('src', src);
    }

    copyText(val: string) {
        const input = this.document.createElement('input');
        this.document.body.appendChild(input);
        if (val) {
            input.value = val;
        } else {
            input.value = 'NA';
        }
        input.select();
        this.document.execCommand('copy');
        input.remove();
    }

    openRefundModel(transaction: Transaction) {
        this.displayRefund = 'block';
        this.transaction = transaction;
    }

    closeRefundModel() {
        this.displayRefund = 'none';
    }

    authenticate() {
        this.closeRefundModel();
        this.loginService.verifyPin({ pin: this.pin }).subscribe(
            (res) => {
                this.pin = '';
                if (res.success) {
                    this.refund();
                } else {
                    this.display = true;
                    this.eMessage = 'Invalid Password.';
                }
            },
            (err) => {
                this.pin = '';
                this.display = true;
                this.eMessage = 'Invalid Password.';
            },
        );
    }

    refund() {
        this.enterpriseService.refund(this.transaction.transactionCode).subscribe(
            (res) => {
                if (res.success) {
                    this.getData();
                    this.displaySuccess = true;
                    this.sMessage = res.status.message;
                } else {
                    this.display = true;
                    this.eMessage = res.status.message;
                }
            },
            (err) => {
                this.display = true;
                this.eMessage = 'Refund Failed';
            },
        );
    }

    /**
     * LP-5360: true when the backend sent an authoritative value for this balance.
     *
     * <p>The template branches on this rather than on the amount, so an authoritative `0` renders
     * as `0.00` while an absent value renders as the unavailable marker. A truthiness check would
     * collapse those two into one, and distinguishing them is the central requirement here.
     */
    hasBalance(snapshot: BalanceSnapshot): boolean {
        return isBalanceAvailable(snapshot);
    }

    /** The value for the `number` pipe; null whenever {@link hasBalance} is false. */
    balanceValue(snapshot: BalanceSnapshot): number | null {
        return balanceAmount(snapshot);
    }
}
